/**
 * Sysadmin Studio Catalog → Tools (scaffolding).
 *
 * Lists system_tools rows + opens a Sheet for create/edit. Polished
 * UI is v1.2; this is the minimum sysadmin can use to author rows
 * without curl.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, useEffect } from 'react';
import {
  Plus, Trash2, Wrench, AlertTriangle, ShieldCheck, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSysAdminSystemTools,
  useCreateSystemTool,
  useUpdateSystemTool,
  useDeleteSystemTool,
  type SystemTool,
} from '@/lib/use-sysadmin-system-catalog';
import { cn } from '@/lib/utils';
// PR-C-MINIMAL — shared constants + helpers extracted to dedupe
// against the other 4 tool authoring surfaces. SOURCE_TYPES used to
// be a 6-tuple here (no `custom` — sysadmin doesn't author tenant
// `custom` rows); the shared tuple is a superset and we filter the
// rendered Select options below.
import {
  SOURCE_TYPES as SHARED_SOURCE_TYPES,
  POLICIES,
} from '@/components/tools-authoring-shared/tools-shared.constants';
import {
  parseJsonTyped,
  tryParseJsonArrayOfStrings,
  validateSlugSysadmin,
} from '@/components/tools-authoring-shared/tools-shared.helpers';

export const Route = createFileRoute('/sysadmin/studio/tools')({
  component: SysadminSystemToolsPage,
});

// Sysadmin system-tools list page renders the source-type Select
// without the tenant-only `custom` label. Mirrors the previous local
// tuple `['builtin', 'mcp', 'openapi', 'http', 'database', 'custom_function']`.
const SOURCE_TYPES = SHARED_SOURCE_TYPES.filter((s) => s !== 'custom');

interface FormState {
  slug: string;
  displayName: string;
  description: string;
  sourceType: string;
  sourceConfigText: string;
  inputSchemaText: string;
  outputSchemaText: string;
  policy: string;
  sideEffectsText: string;
  category: string;
  isActive: boolean;
}

const EMPTY_FORM: FormState = {
  slug: '',
  displayName: '',
  description: '',
  sourceType: 'http',
  sourceConfigText: '{}',
  inputSchemaText: '{}',
  outputSchemaText: '{}',
  policy: 'auto_approve',
  sideEffectsText: '[]',
  category: 'general',
  isActive: true,
};

function rowToForm(r: SystemTool): FormState {
  return {
    slug: r.slug,
    displayName: r.displayName,
    description: r.description,
    sourceType: r.sourceType,
    sourceConfigText: JSON.stringify(r.sourceConfig ?? {}, null, 2),
    inputSchemaText: JSON.stringify(r.inputSchema ?? {}, null, 2),
    outputSchemaText: JSON.stringify(r.outputSchema ?? {}, null, 2),
    policy: r.policy,
    sideEffectsText: JSON.stringify(r.sideEffects ?? [], null, 2),
    category: r.category,
    isActive: r.isActive,
  };
}

function SysadminSystemToolsPage() {
  const { data, isLoading, error } = useSysAdminSystemTools();
  const create = useCreateSystemTool();
  const [editing, setEditing] = useState<SystemTool | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SystemTool | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    if (creating) setForm(EMPTY_FORM);
    else if (editing) setForm(rowToForm(editing));
  }, [creating, editing]);

  const update = useUpdateSystemTool(editing?.id ?? '');
  const del = useDeleteSystemTool();

  const sheetOpen = creating || !!editing;
  const closeSheet = () => {
    setCreating(false);
    setEditing(null);
  };

  const items = useMemo(() => data?.items ?? [], [data]);

  const validateAndBuild = (): { ok: true; body: Partial<SystemTool> } | { ok: false; err: string } => {
    // PR-C-MINIMAL — slug + JSON parsing pulled into shared helpers.
    const slugCheck = validateSlugSysadmin(form.slug);
    if (!slugCheck.ok) return { ok: false, err: slugCheck.err };
    if (!form.displayName.trim()) return { ok: false, err: 'Display name is required.' };
    if (!form.description.trim()) return { ok: false, err: 'Description is required.' };

    const sourceConfigP = parseJsonTyped(form.sourceConfigText || '{}', 'sourceConfig');
    if (!sourceConfigP.ok) return { ok: false, err: sourceConfigP.err };
    const inputSchemaP = parseJsonTyped(form.inputSchemaText || '{}', 'inputSchema');
    if (!inputSchemaP.ok) return { ok: false, err: inputSchemaP.err };
    const outputSchemaP = parseJsonTyped(form.outputSchemaText || '{}', 'outputSchema');
    if (!outputSchemaP.ok) return { ok: false, err: outputSchemaP.err };
    const sideEffectsP = tryParseJsonArrayOfStrings(form.sideEffectsText || '[]', 'sideEffects');
    if (!sideEffectsP.ok) return { ok: false, err: sideEffectsP.err };

    return {
      ok: true,
      body: {
        slug: slugCheck.slug,
        displayName: form.displayName.trim(),
        description: form.description.trim(),
        sourceType: form.sourceType,
        sourceConfig: sourceConfigP.value as Record<string, unknown>,
        inputSchema: inputSchemaP.value as Record<string, unknown>,
        outputSchema: outputSchemaP.value as Record<string, unknown>,
        policy: form.policy as SystemTool['policy'],
        sideEffects: sideEffectsP.value,
        category: form.category.trim() || 'general',
        isActive: form.isActive,
      },
    };
  };

  const handleSave = () => {
    const built = validateAndBuild();
    if (!built.ok) {
      toast.error('Validation', { description: built.err });
      return;
    }
    if (creating) {
      create.mutate(built.body, {
        onSuccess: () => {
          toast.success(`Tool "${built.body.slug}" created`);
          closeSheet();
        },
        onError: (err) => toast.error('Create failed', { description: err.message }),
      });
    } else if (editing) {
      const { slug, ...rest } = built.body;
      void slug;
      update.mutate(rest, {
        onSuccess: () => {
          toast.success(`Tool "${editing.slug}" updated`);
          closeSheet();
        },
        onError: (err) => toast.error('Update failed', { description: err.message }),
      });
    }
  };

  const handleDelete = () => {
    if (!confirmDelete) return;
    del.mutate({ id: confirmDelete.id }, {
      onSuccess: () => {
        toast.success(`Tool "${confirmDelete.slug}" deactivated`);
        setConfirmDelete(null);
      },
      onError: (err) => toast.error('Delete failed', { description: err.message }),
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <Wrench className="h-4 w-4 text-muted-foreground" />
            System Tools
          </h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Sysadmin-authored tools published to every tenant. Tenants see metadata and can grant tools to their agents but cannot view source_config.
          </p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)}>
          <Plus className="h-3.5 w-3.5" /> New tool
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground animate-pulse">Loading…</CardContent></Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load: {error.message}</div>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No system tools yet. Click <strong>New tool</strong> or run the manifest loader.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slug</TableHead>
                  <TableHead>Display name</TableHead>
                  <TableHead className="w-32">Source</TableHead>
                  <TableHead className="w-32">Policy</TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  <TableHead className="w-16">Ver</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((t) => (
                  <TableRow key={t.id} className="cursor-pointer hover:bg-accent/30" onClick={() => setEditing(t)}>
                    <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                    <TableCell className="text-sm">{t.displayName}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{t.sourceType}</Badge></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn('text-[10px] gap-1',
                        t.policy === 'auto_approve' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
                        t.policy === 'require_approval' && 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
                        t.policy === 'forbidden' && 'bg-destructive/10 text-destructive border-destructive/30',
                      )}>
                        {t.policy === 'auto_approve' && <ShieldCheck className="h-2.5 w-2.5" />}
                        {t.policy === 'forbidden' && <Lock className="h-2.5 w-2.5" />}
                        {t.policy.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.isActive ? 'secondary' : 'outline'} className="text-[10px]">
                        {t.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs tabular-nums">v{t.version}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                        onClick={() => setConfirmDelete(t)}
                        title="Deactivate"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Sheet open={sheetOpen} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{creating ? 'New system tool' : `Edit "${editing?.slug}"`}</SheetTitle>
            <SheetDescription>
              Slug is the manifest upsert key (immutable after create). Source config is sysadmin-private — never returned to tenants.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                disabled={!creating}
                placeholder="system.lookup_vendor"
                className="font-mono text-xs h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Display name</Label>
              <Input
                value={form.displayName}
                onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                className="text-sm h-8"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Source type</Label>
                <Select value={form.sourceType} onValueChange={(v) => setForm({ ...form, sourceType: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Policy</Label>
                <Select value={form.policy} onValueChange={(v) => setForm({ ...form, policy: v })}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {POLICIES.map((p) => <SelectItem key={p} value={p}>{p.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Source config (JSON, sysadmin-only)</Label>
              <Textarea
                value={form.sourceConfigText}
                onChange={(e) => setForm({ ...form, sourceConfigText: e.target.value })}
                rows={4}
                className="text-xs font-mono"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Input schema (JSON Schema)</Label>
              <Textarea
                value={form.inputSchemaText}
                onChange={(e) => setForm({ ...form, inputSchemaText: e.target.value })}
                rows={4}
                className="text-xs font-mono"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Output schema (JSON Schema)</Label>
              <Textarea
                value={form.outputSchemaText}
                onChange={(e) => setForm({ ...form, outputSchemaText: e.target.value })}
                rows={3}
                className="text-xs font-mono"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Side effects (JSON string array)</Label>
              <Input
                value={form.sideEffectsText}
                onChange={(e) => setForm({ ...form, sideEffectsText: e.target.value })}
                className="text-xs font-mono h-8"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs">Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="text-sm h-8"
                />
              </div>
              <div className="flex items-center gap-2 h-8">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(v) => setForm({ ...form, isActive: v })}
                />
                <span className="text-xs">Active</span>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3">
              <Button variant="outline" onClick={closeSheet}>Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={create.isPending || update.isPending}
              >
                {creating
                  ? (create.isPending ? 'Creating…' : 'Create tool')
                  : (update.isPending ? 'Saving…' : 'Save changes')}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate system tool</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes <code className="bg-muted px-1 rounded">{confirmDelete?.slug}</code> by setting <code>is_active=false</code>.
              Tenants stop seeing it in their catalog. Existing agent grants stay intact in case the tool is reactivated. Hard delete is not exposed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
